import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { useClientId, useClientConnect } from '@/hooks/client';
import { Item } from '@/types/item';
import { Client } from '@/types/client';
import { Modal } from '@/types/modal';
import { CenterWrapper } from '@/components/atoms/Section';
import { ServiceErrorView } from '@/components/atoms/Error';
import Grid, { GridAction, itemList, itemText } from '@/components/molecules/section/Grid';
import Container from '@/components/molecules/section/Container';
import ModalView from '@/components/modals/Modal';
import { transformDateString } from '@/utilities/datetime';
import { FlagId } from '@/types/common';
import styles from '@/components/atoms/Section.module.scss';

const directShipping = 'direct shipping';
const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);
  const [open, setOpen] = useState(false);

  const flags = item.flags ? item.flags.map(({ id }) => id) : [];

  const connectR = useClientConnect(Number(item.buyerId));

  useEffect(() => {
    if (connectR.kind === 'Loading') {
      setModal({ kind: 'Loading', title: t('modal.client_connect.title'), onClose: () => setModal(null) });
    }

    if (connectR.kind === 'Error') {
      setModal({
        kind: 'Error',
        title: t('modal.client_connect.title'),
        error: connectR.error,
        onClose: () => setModal(null),
      });
    }

    if (connectR.kind === 'Completed') {
      const data = connectR.data;
      const date = new Date();

      // Set it to 15min
      date.setTime(date.getTime() + 15 * 60 * 1000);
      document.cookie = `${data.cookieName}=${data.sessionId};expires=${date.toUTCString()};domain=${
        data.cookieDomain
      };path=/`;
      document.cookie = `vc_uid=${item.buyerId};expires=${date.toUTCString()};domain=${data.cookieDomain};path=/`;

      if (data.redirectTo === undefined || data.redirectTo == null) {
        setModal({
          kind: 'Error',
          title: t('modal.client_connect.title'),
          onClose: () => setModal(null),
        });
      } else {
        window.open(data.redirectTo, '_blank');
        setModal(null);
      }
    }
  }, [connectR.kind]);

  const actions: (GridAction | null)[] = [
    {
      kind: 'Button',
      label: t('item.return_to_warehouse_seller.actions.return_to_warehouse'),
      onClick: () => setModal({ kind: 'ItemReturnToWarehouse', item, onClose: () => setModal(null) }),
      disabled: flags.includes(FlagId.RETURN_TO_WAREHOUSE),
    },
    item.shippingMethod?.toLowerCase() === directShipping
      ? {
          kind: 'Button',
          label: t('item.return_to_warehouse_seller.actions.return_to_seller'),
          onClick: () =>
            setModal({
              kind: 'ItemReturnToSeller',
              isFailure: item.disableReturnToSeller,
              item,
              onClose: () => setModal(null),
            }),
          disabled: flags.includes(FlagId.RETURN_TO_WAREHOUSE) || flags.includes(FlagId.RETURN_TO_SELLER),
        }
      : null,
    {
      kind: 'Button',
      label: t('item.return_to_warehouse_seller.actions.buyer_front_office'),
      onClick: () => {
        connectR.mutate('buyer');
      },
    },
  ];

  return (
    <>
      <Container
        title={t('item.return_to_warehouse_seller.title')}
        testId="return-warehouse-seller"
        actions={actions.filter((i) => i != null)}
        handler={{ open, setOpen }}
      >
        <>
          {item.buyerId && (
            <BodyWithBuyer
              item={item}
              buyerId={Number(item.buyerId)}
            />
          )}
          {!item.buyerId && (
            <Body
              item={item}
              client={null}
            />
          )}
        </>
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

const BodyWithBuyer: React.FC<{ item: Item; buyerId: number }> = ({ item, buyerId }) => {
  const data = useClientId(buyerId);

  return (
    <>
      {data.kind === 'Called' && (
        <Skeleton
          height={100}
          variant="rounded"
        />
      )}
      {data.kind === 'Error' && (
        <CenterWrapper>
          <ServiceErrorView error={data.error} />
        </CenterWrapper>
      )}
      {data.kind === 'Loaded' && (
        <Body
          item={item}
          client={data.data[0]}
        />
      )}
    </>
  );
};

const Body: React.FC<{ item: Item; client: Client | null }> = ({ item, client }) => {
  const { t } = useTranslation();
  const items = [
    itemText(
      t('item.return_to_warehouse_seller.cs_score'),
      client?.csScore,
      undefined,
      [4, 5].includes(client && client.csScore ? client.csScore : 0) ? '#ff5555' : undefined,
    ),
    itemText(t('item.return_to_warehouse_seller.type_of_profile'), client?.customerType),
    itemText(t('item.return_to_warehouse_seller.status'), client?.clientInfoStatus),
    itemText(t('item.return_to_warehouse_seller.client_info'), client?.clientInfoDescription),
    itemText(t('item.return_to_warehouse_seller.shipping_method'), item.shippingMethod),
    itemText(t('item.return_to_warehouse_seller.date_received_by_buyer'), transformDateString(item.dateReceived)),
    itemText(t('item.return_to_warehouse_seller.blocked_by_litigation'), item.isBlockedByLitigation),
    itemText(
      t('item.return_to_warehouse_seller.eligible_for_return'),
      item.eligibleForReturn == null ? '' : item.eligibleForReturn === true ? t('common.yes') : t('common.no'),
    ),
    itemText(
      t('item.return_to_warehouse_seller.return_unlocked'),
      item.unlockReturn ? t('common.yes') : t('common.no'),
    ),
    itemList(
      t('item.return_to_warehouse_seller.flags'),
      item.flags
        ? item.flags.map(({ name }, idx) => (
            <span
              key={idx}
              data-cy={name ? `cell-text-${name.trim().replaceAll(' ', '-').toLocaleLowerCase()}` : 'cell-text'}
              className={styles.text}
            >
              {name}
            </span>
          ))
        : null,
    ),
  ];
  return (
    <Grid
      column={5}
      items={items}
    />
  );
};

export default View;
